/**
 * Metadata builders for the two indexable entity types: DRep profiles and
 * governance actions.
 *
 * Both read from the Elasticsearch catalog rather than resolving anchor URLs
 * over IPFS during metadata generation.
 */

import type { Metadata } from 'next';
import seoConstants from '@src/constants/seo';
import { absoluteUrl } from './config';
import { avatarUrl } from './image';
import { findDRepSeoRecord, findProposal, type DRepSeoRecord } from './catalog';
import { drepDisplayName, drepSummary } from './jsonLd';
import {
  deriveProposalState,
  proposalFacts,
  proposalTypeLabel,
  STATE_SHORT,
  truncate,
  type IndexedProposal,
} from './proposal';

const FALLBACK_IMAGE = seoConstants.socialPreview.bannerImage;

export interface DRepMetadataInput {
  /** Route parameter: a CIP-129/CIP-105 id, a hex id, or a vanity handle. */
  identifier: string;
  /** Path this page is served from, used when the DRep can't be resolved. */
  path: string;
}

/**
 * Always `/drep/<cip129>`.
 *
 * A readable `/<handle>` URL would be the better canonical in principle, but
 * the vanity route does not currently resolve on mainnet — `/api/drep?name=`
 * returns no DRep and no token for every minted handle in the index, so those
 * URLs render a 404. Pointing canonicals at them would hand search engines a
 * set of dead targets. Revisit once name lookup works on mainnet.
 */
export function canonicalDRepPath(record: DRepSeoRecord): string {
  return `/drep/${record.drepId}`;
}

export async function buildDRepMetadata({
  identifier,
  path,
}: DRepMetadataInput): Promise<Metadata> {
  const record = await findDRepSeoRecord(identifier).catch(() => null);

  if (!record) {
    return {
      title: 'DRep profile',
      description: seoConstants.appDesc,
      alternates: { canonical: path },
      // Unresolvable profiles shouldn't accumulate in the index.
      robots: { index: false, follow: true },
    };
  }

  const canonical = canonicalDRepPath(record);
  const name = drepDisplayName(record);
  const summary = drepSummary(record);
  const title = `${name} — Cardano DRep`;
  const description = summary
    ? truncate(summary, 155)
    : `${name} is a registered Cardano delegated representative. See their objectives, voting record and voting power, then delegate your ADA voting power on DRep ID.`;

  // Never the raw `record.image`. An `http(s)` value passes through; a base64
  // data URI (12 mainnet DReps, up to 262 KB) becomes `/drep-avatar/<id>`,
  // which serves the same bytes as a fetchable resource — scrapers discard a
  // data URI outright, so those profiles used to share with no card at all.
  // Absolute, because og:image must be.
  const resolved = avatarUrl(record);
  const image = resolved ? absoluteUrl(resolved) : FALLBACK_IMAGE;

  return {
    title,
    description,
    alternates: { canonical },
    keywords: [
      name,
      `${name} drep`,
      'cardano drep',
      'delegate voting power',
      record.drepId,
    ],
    openGraph: {
      type: 'profile',
      title,
      description,
      url: absoluteUrl(canonical),
      images: [{ url: image, alt: `${name} — Cardano DRep` }],
      siteName: seoConstants.appName,
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      images: [image],
    },
  };
}

export function drepMetadataRecord(
  identifier: string
): Promise<DRepSeoRecord | null> {
  return findDRepSeoRecord(identifier).catch(() => null);
}

export async function buildProposalMetadata(
  id: string,
  path: string
): Promise<Metadata> {
  const proposal = await findProposal(id).catch(() => null);

  if (!proposal) {
    return {
      title: 'Governance action',
      description: seoConstants.appDesc,
      alternates: { canonical: path },
      robots: { index: false, follow: true },
    };
  }

  return proposalMetadataFrom(proposal, path);
}

export function proposalMetadataFrom(
  proposal: IndexedProposal,
  path: string
): Metadata {
  const state = deriveProposalState(proposal);
  const typeLabel = proposalTypeLabel(proposal.proposal?.type);
  const rawTitle = proposal.meta?.title?.trim();

  // The state belongs in the title: "enacted" vs "expired" is the single most
  // useful thing a searcher can learn from a result for a past proposal.
  const title = rawTitle
    ? `${truncate(rawTitle, 78)} — ${STATE_SHORT[state]}`
    : `${typeLabel} ${proposal.id.slice(0, 18)}… — ${STATE_SHORT[state]}`;

  const description = proposal.meta?.abstract
    ? truncate(proposal.meta.abstract, 155)
    : truncate(
        `${proposalFacts(proposal)}. Full voting breakdown across DReps, SPOs and the Constitutional Committee on DRep ID.`,
        155
      );

  return {
    // `absolute` opts out of the "| DRep ID" template. A proposal title is
    // already a specific document name near the ~60-character limit Google
    // renders; a brand suffix would only push the status out of view.
    title: { absolute: title },
    description,
    alternates: { canonical: path },
    keywords: [
      rawTitle || typeLabel,
      typeLabel.toLowerCase(),
      'cardano governance action',
      `governance action ${state}`,
      proposal.id,
    ],
    openGraph: {
      type: 'article',
      title,
      description,
      url: absoluteUrl(path),
      publishedTime: proposal.createdAt?.time || undefined,
      siteName: seoConstants.appName,
      images: [{ url: FALLBACK_IMAGE, alt: title }],
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      images: [FALLBACK_IMAGE],
    },
    other: {
      // Surfaced for AI answer engines and any consumer reading raw meta.
      'governance-action-status': state,
      'governance-action-type': typeLabel,
    },
  };
}
